// src/components/admin/TorreDeControl/modals/DataModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Upload, Image as ImageIcon, Trash2, Calendar, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import DateSelector from '../DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

// Districts that must use afternoon slot (too far for morning pickup)
const AFTERNOON_ONLY_DISTRICTS = [
  'Carabayllo',
  'Ventanilla',
  'Puente Piedra',
  'Villa El Salvador',
  'Villa María del Triunfo',
  'Chorrillos',
  'Ate',
  'San Juan de Lurigancho'
];

function DataModal({ item, itemType, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    notes: '',
    notes_driver: '',
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Check if order's district is afternoon-only
  const isAfternoonOnlyDistrict = itemType === 'order' && item?.address?.district && 
    AFTERNOON_ONLY_DISTRICTS.some(
      district => district.toLowerCase() === item.address.district.toLowerCase()
    );

  useEffect(() => {
    if (item) {
      setFormData({
        notes: item.notes || item.order_notes || '',
        notes_driver: item.notes_driver || item.driver_notes || '',
      });
      setExistingImages(item.image_urls || []);
      
      // Set delivery date if it exists (for deliveries)
      if (itemType === 'delivery' && item.delivery_date) {
        // Convert to YYYY-MM-DD format if needed
        const dateStr = item.delivery_date.split('T')[0];
        setSelectedDate(dateStr);
      }

      // ✨ NEW: Set pickup time slot if it exists (for orders)
      if (itemType === 'order' && item.pickup_time_slot) {
        setSelectedTimeSlot(item.pickup_time_slot);
      }
    }
  }, [item, itemType]);

  // Handle paste event for images
  useEffect(() => {
    function handlePaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            addImageFile(file);
          }
        }
      }
    }

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  function addImageFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImages((prev) => [
        ...prev,
        {
          file,
          preview: e.target.result,
          name: file.name,
        },
      ]);
    };
    reader.readAsDataURL(file);
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    files.forEach((file) => addImageFile(file));
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    files.forEach((file) => addImageFile(file));
  }

  function removeImage(index) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  function formatDateForDisplay(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Prepare update data
      const updateData = { ...formData };
      
      // Add delivery_date if it's a delivery and date was changed
      if (itemType === 'delivery' && selectedDate) {
        updateData.delivery_date = selectedDate;
      }

      // ✨ NEW: Add pickup_time_slot if it's an order
      if (itemType === 'order' && selectedTimeSlot) {
        updateData.pickup_time_slot = selectedTimeSlot;
      }

      // Update notes, delivery_date, and/or pickup_time_slot
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/admin/delivery-point/${item.id}/data`
          : `${API_URL}/api/admin/order/${item.id}/data`;

      await axios.put(endpoint, updateData);

      // Upload images if any
      if (images.length > 0) {
        const formDataImages = new FormData();
        images.forEach((img) => {
          formDataImages.append('images', img.file);
        });

        const uploadEndpoint =
          itemType === 'delivery'
            ? `${API_URL}/api/upload_image/${item.id}`
            : `${API_URL}/api/upload_image_order/${item.id}`;

        await axios.post(uploadEndpoint, formDataImages, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating data:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Error al actualizar información');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  // Check if date has changed (for deliveries)
  const hasDateChanged = itemType === 'delivery' && 
    item.delivery_date && 
    selectedDate && 
    item.delivery_date.split('T')[0] !== selectedDate;

  // Check if time slot has changed (for orders)
  const hasTimeSlotChanged = itemType === 'order' && 
    item.pickup_time_slot && 
    selectedTimeSlot && 
    item.pickup_time_slot !== selectedTimeSlot;

  // Time slot configurations
  const timeSlotOptions = [
    {
      value: 'morning',
      label: 'Mañana (6am - 9am)',
      pickupTime: '6:00 AM - 9:00 AM',
      deliveryTime: '10:00 AM - 8:00 PM',
      icon: '☀️',
      color: 'amber',
      disabled: isAfternoonOnlyDistrict
    },
    {
      value: 'afternoon',
      label: 'Tarde (11am - 1pm)',
      pickupTime: '11:00 AM - 1:00 PM',
      deliveryTime: '3:00 PM - 8:00 PM',
      icon: '🌙',
      color: 'purple',
      disabled: false
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-brand-primary px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <FileText className="w-5 h-5" />
            Actualizar Datos de {itemType === 'delivery' ? 'Entrega' : 'Recojo'}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Delivery Date Selector - Only for deliveries */}
          {itemType === 'delivery' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Fecha de Entrega
              </label>
              <DateSelector
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                showCalendar={showCalendar}
                onToggleCalendar={() => setShowCalendar(!showCalendar)}
              />
              
              {/* Show warning if date changed */}
              {hasDateChanged && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 font-montserrat">
                    <strong>⚠️ Atención:</strong> Estás cambiando la fecha de entrega de{' '}
                    <strong>{formatDateForDisplay(item.delivery_date.split('T')[0])}</strong> a{' '}
                    <strong>{formatDateForDisplay(selectedDate)}</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ✨ NEW: Pickup Time Slot Selector - Only for orders */}
          {itemType === 'order' && (
            <div>
              <label className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700 font-montserrat">
                <Clock className="w-4 h-4" />
                Horario de Recojo
              </label>
              
              <div className="space-y-2">
                {timeSlotOptions.map((slot) => {
                  const isSelected = selectedTimeSlot === slot.value;
                  const colorClasses = slot.color === 'amber' 
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-purple-300 bg-purple-50 text-purple-700';
                  
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => !slot.disabled && setSelectedTimeSlot(slot.value)}
                      disabled={slot.disabled}
                      className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                        slot.disabled
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                          : isSelected
                          ? colorClasses
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{slot.icon}</span>
                            <span className="text-sm font-bold font-montserrat">
                              {slot.label}
                            </span>
                            {slot.disabled && (
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">
                                No disponible
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs ml-7">
                            <div>
                              <span className="text-slate-500">Recojo:</span>
                              <span className="ml-1 font-medium">{slot.pickupTime}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Entrega:</span>
                              <span className="ml-1 font-medium">{slot.deliveryTime}</span>
                            </div>
                          </div>
                        </div>
                        {!slot.disabled && (
                          <div className={`flex-shrink-0 ml-3 ${
                            isSelected ? slot.color === 'amber' ? 'text-amber-500' : 'text-purple-500' : 'text-slate-300'
                          }`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected 
                                ? slot.color === 'amber' 
                                  ? 'border-amber-500 bg-amber-500' 
                                  : 'border-purple-500 bg-purple-500'
                                : 'border-slate-300'
                            }`}>
                              {isSelected && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* District restriction notice */}
              {isAfternoonOnlyDistrict && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700 font-montserrat">
                    Debido a la distancia de <strong>{item.address?.district}</strong>, solo está disponible el horario de tarde.
                  </p>
                </div>
              )}

              {/* Show warning if time slot changed */}
              {hasTimeSlotChanged && (
                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 font-montserrat">
                    <strong>⚠️ Atención:</strong> Estás cambiando el horario de recojo de{' '}
                    <strong>
                      {item.pickup_time_slot === 'morning' ? 'Mañana (6-9am)' : 'Tarde (11am-1pm)'}
                    </strong> a{' '}
                    <strong>
                      {selectedTimeSlot === 'morning' ? 'Mañana (6-9am)' : 'Tarde (11am-1pm)'}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Notas del Cliente
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary font-montserrat resize-none"
              placeholder="Agregar notas sobre la entrega..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Notas del Motorizado
            </label>
            <textarea
              name="notes_driver"
              value={formData.notes_driver}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary font-montserrat resize-none"
              placeholder="Notas adicionales del motorizado..."
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Imágenes
            </label>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-slate-600 mb-2 font-montserrat">
                  Imágenes existentes: {existingImages.length}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {existingImages.map((url, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={url}
                        alt={`Existente ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-slate-200"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-brand-primary bg-brand-primary/5'
                  : 'border-slate-300 hover:border-brand-primary'
              }`}
            >
              <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-2 font-montserrat">
                Arrastra imágenes aquí, pégalas con Ctrl+V, o
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors font-montserrat inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Selecciona archivos
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* New Images Preview */}
            {images.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-slate-600 mb-2 font-montserrat">
                  Nuevas imágenes a subir: {images.length}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <div key={index} className="relative aspect-square group">
                      <img
                        src={img.preview}
                        alt={img.name}
                        className="w-full h-full object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-brand-primary hover:bg-brand-primary/90 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DataModal;